import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import Papa from 'papaparse';

import Sheet from 'components/core/Sheet';
import Typography from 'components/core/Typography';
import Stack from 'components/core/Stack';
import Button from 'components/core/Button';
import Grid from 'components/core/Grid';
import Autocomplete from 'components/core/Autocomplete';
import ProcessingOverlay from 'components/core/ProcessingOverlay';

import Accordion from '@mui/joy/Accordion';
import AccordionDetails from '@mui/joy/AccordionDetails';
import AccordionSummary from '@mui/joy/AccordionSummary';

import ImportedObservations from 'components/ImportedObservations';

import lookupTableActions from 'actions/lookupTableActions';
import personActions from 'actions/personActions';

import {
    fieldsToMap,
    classMap,
    cleanParsedData,
    attemptMapping,
    mapAndValidateRows,
    saveObservation
} from 'utils/ObservationImportUtils';

const mappingDefaults = fieldsToMap.reduce((acc, field) => {
    acc[field.field] = null;
    return acc;
}, {});

const ObservationImport = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const observationOptions = useSelector(({ lookupTable }) => lookupTable.options);
    const personList = useSelector(({ person }) => person.list);
    const [uploadData, setUploadData] = useState([]);
    const [uploadFields, setUploadFields] = useState([]);
    const [mapping, setMapping] = useState(mappingDefaults);
    const [observations, setObservations] = useState([]);
    const [erroredObservations, setErroredObservations] = useState([]);
    const [expanded, setExpanded] = useState('upload');

    const [isUploadComplete, setIsUploadComplete] = useState(false);
    const [isMappingComplete, setIsMappingComplete] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const observationNoteTypes = useMemo(() => {
        return observationOptions.noteTypes?.filter(noteType =>
            noteType.get('filteredFor') === 'Observation'
        ) || [];
    }, [observationOptions.noteTypes]);

    const fieldsToMapWithNotes = useMemo(() => {
        return [
            ...fieldsToMap,
            ...observationNoteTypes.map(noteType => ({
                field: `note_${noteType.id}`,
                label: `Note: ${noteType.get('label')}`,
                type: 'Note'
            }))
        ];
    }, [observationNoteTypes]);

    useEffect(() => {
        dispatch(lookupTableActions.fetchAllOptions());
        dispatch(personActions.list());
    }, [dispatch]);

    useEffect(() => {
        if (observationNoteTypes.length > 0) {
            setMapping(prev => {
                const newMapping = { ...prev };
                observationNoteTypes.forEach(noteType => {
                    const fieldKey = `note_${noteType.id}`;
                    if (!(fieldKey in newMapping)) {
                        newMapping[fieldKey] = null;
                    }
                });
                return newMapping;
            });
        }
    }, [observationNoteTypes]);

    useEffect(() => {
        if (isUploadComplete && isMappingComplete) {
            setExpanded('review');
        } else if (isUploadComplete) {
            setExpanded('mapping');
        } else {
            setExpanded('upload');
        }
    }, [isUploadComplete, isMappingComplete]);

    useEffect(() => {
        if (isMappingComplete && observations.length === 0) {
            handleReview();
        }
    }, [isMappingComplete, mapping]);

    const handleChangeAccordion = (panel) => {
        const pannelCriteria = {
            upload: [],
            mapping: [isUploadComplete],
            review: [isUploadComplete, isMappingComplete]
        }

        if (pannelCriteria[panel].every((c) => c)) {
            return (_, isExpanded) => {
                setExpanded(isExpanded ? panel : false);
            }
        }
    }

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: 'greedy',
                complete: (results) => {
                    const { data, fields } = cleanParsedData(results);
                    setUploadData(data);
                    setUploadFields(fields);
                    const { mapping: newMapping, isComplete } = attemptMapping(fields, mapping);
                    setMapping(newMapping);
                    if (isComplete !== isMappingComplete) {
                        setIsMappingComplete(isComplete);
                    }
                    setIsUploadComplete(true);
                },
                error: (error) => {
                    console.error('Error parsing CSV:', error);
                }
            });
        }
    };

    const renderUpload = () => {
        return (
            <Stack spacing={2}>
                <Button component="label">
                    Select CSV File
                    <input
                        type="file"
                        hidden
                        accept=".csv"
                        onChange={handleFileUpload}
                    />
                </Button>
                {uploadFields.length > 0 && (
                    <Typography>
                        File uploaded successfully with {uploadFields.length} columns
                    </Typography>
                )}
            </Stack>
        )

    }

    const handleChangeMapping = (field, value) => {
        let isNewMappingComplete = false;
        const newMapping = {
            ...mapping,
            [field]: value
        };
        setMapping(newMapping);
        if (Object.values(newMapping).every((v) => v !== null)) {
            isNewMappingComplete = true;
        }

        if (isNewMappingComplete !== isMappingComplete) {
            setIsMappingComplete(isNewMappingComplete);
        }
    }

    const renderFieldMappingWarnings = () => {
        const requiredFields = ['name', 'observedBy'];
        const missingRequired = requiredFields.filter(field => !mapping[field]);
        const missingDateAndEvent = !mapping['date'] && !mapping['event'];
        const missingLocationAndCoords = !mapping['location'] && (!mapping['lat'] || !mapping['lng']);

        if (missingRequired.length > 0 || missingDateAndEvent || missingLocationAndCoords) {
            const warnings = [];

            if (missingRequired.length > 0) {
                warnings.push(`Missing required fields: ${missingRequired.join(', ')}`);
            }

            if (missingDateAndEvent) {
                warnings.push('Either Date or Event field must be mapped');
            }

            if (missingLocationAndCoords) {
                warnings.push('Either Location or both Latitude and Longitude must be mapped');
            }

            return (
                <div style={{
                    padding: '12px',
                    backgroundColor: '#fdf0e1',
                    border: '1px solid #f3c896',
                    borderRadius: '8px',
                    marginTop: '16px'
                }}>
                    <Typography variant="body2" color="warning">
                        <strong>⚠️ Field Mapping Warnings:</strong>
                        {warnings.map((warning, index) => (
                            <div key={index} style={{ marginLeft: '8px' }}>• {warning}</div>
                        ))}
                        <div style={{ marginTop: '8px', fontStyle: 'italic' }}>
                            Observations with missing required data will appear as errors in the Review section.
                        </div>
                    </Typography>
                </div>
            );
        }
        return null;
    };

    const renderFieldMapping = () => {
        if (!uploadFields.length) {
            return null;
        }

        return (
            <Stack spacing={2}>
                <Grid container spacing={2}>
                    {fieldsToMapWithNotes.map((field, index) => (
                        <React.Fragment key={field.field}>
                            <Grid item xs={6} sx={{
                                backgroundColor: index % 2 === 0 ? 'transparent' : 'primary.50',
                                py: 1,
                                px: 2,
                                borderRadius: 1
                            }}>
                                <Typography>{field.label}</Typography>
                            </Grid>
                            <Grid item xs={6} sx={{
                                backgroundColor: index % 2 === 0 ? 'transparent' : 'primary.50',
                                py: 1,
                                px: 2,
                                borderRadius: 1
                            }}>
                                <Autocomplete
                                    value={mapping[field.field] || null}
                                    options={uploadFields}
                                    getOptionLabel={(option) => option}
                                    onChange={(value) => {
                                        handleChangeMapping(field.field, value);
                                    }}
                                />
                            </Grid>
                        </React.Fragment>
                    ))}
                </Grid>
                {renderFieldMappingWarnings()}
                {!isMappingComplete && (
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setIsMappingComplete(true);
                        }}
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        Continue with Partial Mapping
                    </Button>
                )}
            </Stack>
        )
    }

    const downloadFile = (blob, filename) => {
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
            window.navigator.msSaveOrOpenBlob(blob, filename);
        } else {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            setTimeout(() => {
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }, 0);
        }
    };

    const downloadErroredRows = (erroredFieldOptions, erroredRows) => {
        const today = new Date();
        const dateStr = today.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        }).replace(/\//g, '-');

        const failedObservationsCSV = Papa.unparse(erroredRows);
        const failedBlob = new Blob([failedObservationsCSV], { type: 'text/csv;charset=utf-8' });
        downloadFile(failedBlob, `${dateStr}-failed-boriis-upload-observations.csv`);

        const fieldOptionsColumns = {};
        erroredFieldOptions.forEach(fieldOption => {
            Object.entries(fieldOption).forEach(([field, values]) => {
                if (Array.isArray(values)) {
                    fieldOptionsColumns[field] = values;
                }
            });
        });

        const maxLength = Math.max(...Object.values(fieldOptionsColumns).map(arr => arr.length));

        const fieldOptionsRows = Array.from({ length: maxLength }, (_, i) => {
            const row = {};
            Object.entries(fieldOptionsColumns).forEach(([field, values]) => {
                row[field] = values[i] || '';
            });
            return row;
        });
        if (fieldOptionsRows.length > 0) {
            const fieldOptionsCSV = Papa.unparse(fieldOptionsRows);
            const optionsBlob = new Blob([fieldOptionsCSV], { type: 'text/csv;charset=utf-8' });
            setTimeout(() => {
                downloadFile(optionsBlob, 'boriis-field-options.csv');
            }, 100);
        }
    };

    const finalize = async (editedObservations = []) => {
        const erroredCols = []
        const erroredRows = []
        erroredObservations.forEach(({ row, errors }) => {
            erroredRows.push(row);
            Object.keys(errors).forEach((key) => {
                if (!erroredCols.includes(key)) {
                    erroredCols.push(key);
                }
            });
        });

        const erroredFieldOptions = [];
        erroredCols.forEach((col) => {
            const erroredFieldData = fieldsToMap.find((field) => field.field === col);

            if (erroredFieldData?.type?.type === 'Pointer') {
                let options
                if (erroredFieldData.type.targetClass === 'Person') {
                    options = personList.map((personName) => {
                        const person = personName.get('person');
                        return person.get('firstName') + ' ' + person.get('lastName')
                    });
                } else {
                    const classArrayIdentifier = classMap[erroredFieldData.type.targetClass];
                    const classArray = observationOptions[classArrayIdentifier];
                    options = classArray.map((item) => item.get('label'));
                }
                erroredFieldOptions.push({
                    [col]: options
                });
            }
        })
        if (erroredObservations.length > 0 && editedObservations.length === 0) {
            downloadErroredRows(erroredFieldOptions, erroredRows);
        }

        const allObservationsToSave = [
            ...observations.map(({ observation, notes }) => ({ observation, notes })),
            ...editedObservations.map(observation => ({ observation, notes: [] }))
        ];

        await Promise.all(allObservationsToSave.map(({ observation, notes }) =>
            saveObservation(observation, notes, dispatch)
        ));
        navigate('/');
    }

    const handleReview = async () => {
        setIsProcessing(true);
        const { valid, errors } = await mapAndValidateRows(uploadData, mapping, {
            dispatch,
            observationOptions,
            personList
        });
        setObservations(valid);
        setErroredObservations(errors);
        setIsProcessing(false);
    }

    return (
        <Sheet sx={{ p: 4 }}>
            <ProcessingOverlay enabled={isProcessing} caption="Matching observations..." />
            <Stack spacing={4}>
                <Typography variant="h4">Import Observations</Typography>
                <Accordion expanded={expanded === 'upload'} onChange={handleChangeAccordion('upload')}>
                    <AccordionSummary>Upload CSV</AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={2} sx={{p: 2}}>
                            {renderUpload()}
                        </Stack>
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    expanded={expanded === 'mapping'}
                    onChange={handleChangeAccordion('mapping')}
                    disabled={!uploadFields.length}
                >
                    <AccordionSummary>Field Mapping</AccordionSummary>
                    <AccordionDetails>
                        <Stack spacing={2} sx={{p: 2}}>
                            {renderFieldMapping()}
                        </Stack>
                    </AccordionDetails>
                </Accordion>

                <Accordion
                    expanded={expanded === 'review'}
                    onChange={handleChangeAccordion('review')}
                    disabled={!isMappingComplete}
                >
                    <AccordionSummary>Review</AccordionSummary>
                    <AccordionDetails>
                        <ImportedObservations
                            observations={observations}
                            erroredObservations={erroredObservations}
                            mapping={mapping}
                            finalize={finalize}
                        />
                    </AccordionDetails>
                </Accordion>
            </Stack>
        </Sheet>
    );
};

export default ObservationImport;
